import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux'
import { setEndChatConfirm, endChat } from 'store/app/app.actions';
import style from './endchatdialog.module.scss';
import useApp from 'hooks/useApp';
import { turnOffThenSetAriaLiveValue } from 'utils'

interface Props { 
    endChatConfirm: boolean,
}

const ANIMATION_DURATION = 500;

const EndChatDialog: React.FC<Props> = (props) => {
    const dispatch = useDispatch();
    const { language, appTextTranslations } = useApp();
    const endButtonRef = useRef<HTMLButtonElement | null>(null);
    const [isVisible, setIsVisible] = useState(props.endChatConfirm);

    useEffect(() => {
        if (props.endChatConfirm) {
            setIsVisible(true)
            endButtonRef.current?.focus();
        }
    }, [props.endChatConfirm]);
    
    useEffect(() => {
        if (!isVisible) {
            const timeoutId = setTimeout(() => {
                dispatch(setEndChatConfirm(false));
            }, ANIMATION_DURATION);

            return () => {
                clearTimeout(timeoutId);
            };
        } else {
            //Removing aria-live attribute from chat wrapper helps to ignore reading elements in DOM or escape reading them twice when they are in focus
            turnOffThenSetAriaLiveValue('sa-chat-app')
        }
    }, [isVisible]);

    const endChatText =
        appTextTranslations?.endChatDialogue?.[language].endChatText ??
        'Are you sure you want to end the chat?';
    const endChatCancelMessage =
        appTextTranslations?.endChatDialogue?.[language].endChatCancel ?? 'Cancel';
    const endChatConfirmMessage =
        appTextTranslations?.endChatDialogue?.[language].endChatConfirm ?? 'End Chat';

    const onClick = (e: React.MouseEvent) => {
        if (!props.endChatConfirm) return;

        if ((e.target as HTMLElement).classList[0] === style.overlay) {
            setIsVisible(false)
        }
    }

    if (!props.endChatConfirm) return null

    return (
        <div onClick={onClick} className={`${isVisible ? style.overlay : `${style.overlay} ${style.closed}`}`} role="dialog" aria-modal="true" aria-describedby='modal-description'>
            <div className={`${isVisible ? style.wrapper : `${style.wrapper} ${style.closed}`}`}>
                <div className={style.endChatOptions}>
                    <p id="modal-description" className={style.message}>{endChatText}</p>
                    <div className={style.endChatOptionsRow}>
                        <button ref={endButtonRef} className={style.cancelButton} onClick={() => setIsVisible(false)}>
                            {endChatCancelMessage}
                        </button>
                        <button className={style.okButton} onClick={() => dispatch(endChat())}>
                            {endChatConfirmMessage}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EndChatDialog;